import { Box } from '@ouster/webviz-react-components';

import { AccessStatus } from '../../types';

interface AccessOptionProps {
  status: AccessStatus;
  title: string;
  description: string;
  isSelected: boolean;
  onSelect: (status: AccessStatus) => void;
  extraConfig?: React.ReactNode;
}

const AccessOption = ({
  status,
  title,
  description,
  isSelected,
  onSelect,
  extraConfig,
}: AccessOptionProps) => (
  <Box
    role="group"
    p={4}
    rounded={2}
    border={1}
    borderColor={isSelected ? 'blue.300' : 'whiteAlpha.300'}
    borderStyle="solid"
    cursor={isSelected ? 'default' : 'pointer'}
    onClick={() => onSelect(status)}
    display="grid"
    flexDirection={extraConfig ? 'column' : 'row'}
    _hover={!isSelected ? { borderColor: 'blue.300' } : {}}
    data-testid={`access-option-${status.toLowerCase()}`}
  >
    <Box
      display="flex"
      alignItems="center"
      placeContent="space-between"
      columnGap={4}
    >
      <Box display="grid" rowGap={2}>
        <Box as="p" fontWeight="semibold">
          {title}
        </Box>
        <Box as="p" color="gray.300" fontSize={14}>
          {description}
        </Box>
      </Box>
      <Box
        width={8}
        height={8}
        borderRadius="100%"
        border="2px solid"
        borderColor={isSelected ? 'transparent' : 'gray.400'}
        bgColor={isSelected ? 'blue.300' : 'transparent'}
        _groupHover={
          !isSelected
            ? { borderColor: 'blue.300' }
            : { borderColor: 'transparent' }
        }
      />
    </Box>
    {isSelected && extraConfig}
  </Box>
);

interface AccessLevelSelectorProps {
  selectedStatus: AccessStatus;
  onStatusChange: (status: AccessStatus) => void;
  renderPublicConfig: () => React.ReactNode;
  renderLimitedConfig: () => React.ReactNode;
  renderUnpublishConfirmation: () => React.ReactNode;
}

export const AccessLevelSelector = ({
  selectedStatus,
  onStatusChange,
  renderPublicConfig,
  renderLimitedConfig,
  renderUnpublishConfirmation,
}: AccessLevelSelectorProps) => (
  <Box display="grid" rowGap={4} data-testid="access-level-selector-root">
    <AccessOption
      status="PRIVATE"
      title="Private"
      description="Only your organization members can access this recording."
      isSelected={selectedStatus === 'PRIVATE'}
      onSelect={onStatusChange}
      extraConfig={renderUnpublishConfirmation()}
    />

    <AccessOption
      status="PUBLIC_PUBLIC_FEATURED"
      title="Public Collections: Non-logged-in Users (Featured)"
      description="Featured on the page for all public visitors. Uses an image and marketing title."
      isSelected={selectedStatus === 'PUBLIC_PUBLIC_FEATURED'}
      onSelect={onStatusChange}
      extraConfig={renderPublicConfig()}
    />
    <AccessOption
      status="PUBLIC_PUBLIC_NON_FEATURED"
      title="Public Collections: Non-logged-in Users (List)"
      description="Display in the list at the bottom of the page for all public visitors."
      isSelected={selectedStatus === 'PUBLIC_PUBLIC_NON_FEATURED'}
      onSelect={onStatusChange}
    />

    <AccessOption
      status="PRIVATE_PUBLIC"
      title="Public Collections: Logged-in Users"
      description="Visible only to authenticated members within the list on the page."
      isSelected={selectedStatus === 'PRIVATE_PUBLIC'}
      onSelect={onStatusChange}
    />
    <AccessOption
      status="PARTNER_PREVIEW"
      title="Partner Previews: Selected Organizations"
      description="Available only to specific organizations on their partner preview page."
      isSelected={selectedStatus === 'PARTNER_PREVIEW'}
      onSelect={onStatusChange}
      extraConfig={
        <>
          {renderUnpublishConfirmation()}
          {renderLimitedConfig()}
        </>
      }
    />
  </Box>
);
